import { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const ChannelApiContext = createContext(null);

export function ChannelApiProvider({ children }) {
  const { get, put, del, post } = useApiService(CONFIG.SERVER_CHANNEL);
  const { get: get_assistant } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getChannels = useCallback(
    async (skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(`/v1/channels?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching channels:', error);
        throw error;
      }
    },
    [get],
  );

  const getChannelById = useCallback(
    async (channelId) => {
      try {
        const response = await get(`/v1/channels/${channelId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching channel by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getChannelSecretsById = useCallback(
    async (channelId) => {
      try {
        const response = await get(`/v1/channels/${channelId}/secrets`);
        return response.data;
      } catch (error) {
        console.error('Error fetching channel secrets by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getAssistants = useCallback(async () => {
    try {
      const response = await get_assistant(`/v1/assistants`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching assistants:', error);
      throw error;
    }
  }, [get]);

  const getAssistantByChannelId = useCallback(
    async (channelId) => {
      try {
        const response = await get(`/v1/channels/${channelId}/assistant`);
        return response.data;
      } catch (error) {
        console.error('Error fetching assistant by channel ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createChannel = useCallback(
    async (channel) => {
      try {
        const response = await put(`/v1/channels`, channel);
        return response.data;
      } catch (error) {
        console.error('Error creating channel:', error);
        throw error;
      }
    },
    [put],
  );

  const updateChannel = useCallback(
    async (channelId, channel) => {
      try {
        const response = await put(`/v1/channels/${channelId}`, channel);
        return response.data;
      } catch (error) {
        console.error('Error updating channel:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteChannel = useCallback(
    async (channelId) => {
      try {
        const response = await del(`/v1/channels/${channelId}`);
        return response.data.item;
      } catch (error) {
        console.error('Error deleting channel:', error);
        throw error;
      }
    },
    [del],
  );

  const getChannelOptions = useCallback(async () => {
    try {
      const response = await get(`/v1/channels/options`);
      return response.data;
    } catch (error) {
      console.error('Error fetching channel options:', error);
      throw error;
    }
  }, [get]);

  const getTriggers = useCallback(
    async (channel_id, skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(
          `/v1/channels/${channel_id}/triggers/all?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching triggers:', error);
        throw error;
      }
    },
    [get],
  );

  const getThematics = useCallback(async () => {
    try {
      const response = await get(`/v1/thematics`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching thematics:', error);
      throw error;
    }
  }, [get]);

  const deleteTrigger = useCallback(
    async (channelId, triggerID) => {
      try {
        const response = await del(`/v1/channels/${channelId}/triggers/${triggerID}`);
        return response.data.item;
      } catch (error) {
        console.error('Error deleting trigger:', error);
        throw error;
      }
    },
    [del],
  );

  const createTrigger = useCallback(
    async (channelId, trigger) => {
      try {
        const response = await post(`/v1/channels/${channelId}/triggers`, trigger);
        return response.data;
      } catch (error) {
        console.error('Error creating trigger:', error);
        throw error;
      }
    },
    [post],
  );

  const updateTrigger = useCallback(
    async (channelId, triggerID, trigger) => {
      try {
        const response = await put(`/v1/channels/${channelId}/triggers/${triggerID}`, trigger);
        return response.data;
      } catch (error) {
        console.error('Error updating trigger:', error);
        throw error;
      }
    },
    [put],
  );

  const getSearchTriggers = useCallback(
    async (channelId, search, skip, limit) => {
      if (!limit) {
        limit = 10;
      }
      if (!skip) {
        skip = 0;
      }
      if (!search) {
        search = '';
      }
      try {
        const response = await get(
          `/v1/channels/${channelId}/triggers/search_triggers?search=${search}&skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching trigger', error);
        throw error;
      }
    },
    [get],
  );

  const getNotifications = useCallback(async () => {
    try {
      const response = await get_assistant(`/v1/notifications`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }, [get]);

  // Guardrail API methods
  const getGuardrails = useCallback(
    async (channelId, skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(
          `/v1/channels/${channelId}/guardrails/all?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching guardrails:', error);
        throw error;
      }
    },
    [get],
  );

  const getGuardrailById = useCallback(
    async (channelId, guardrailKey) => {
      try {
        const response = await get(`/v1/channels/${channelId}/guardrails/${guardrailKey}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching guardrail:', error);
        throw error;
      }
    },
    [get],
  );

  const createGuardrail = useCallback(
    async (channelId, guardrail) => {
      try {
        const response = await post(`/v1/channels/${channelId}/guardrails`, guardrail);
        return response.data;
      } catch (error) {
        console.error('Error creating guardrail:', error);
        throw error;
      }
    },
    [post],
  );

  const updateGuardrail = useCallback(
    async (channelId, guardrailKey, guardrail) => {
      try {
        const response = await put(
          `/v1/channels/${channelId}/guardrails/${guardrailKey}`,
          guardrail,
        );
        return response.data;
      } catch (error) {
        console.error('Error updating guardrail:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteGuardrail = useCallback(
    async (channelId, guardrailKey) => {
      try {
        const response = await del(`/v1/channels/${channelId}/guardrails/${guardrailKey}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting guardrail:', error);
        throw error;
      }
    },
    [del],
  );

  const addGuardrailThematic = useCallback(
    async (channelId, guardrailKey, thematicKey, confidenceLevel) => {
      try {
        const response = await post(
          `/v1/channels/${channelId}/guardrails/${guardrailKey}/thematics`,
          {
            thematic_key: thematicKey,
            confidence_level: confidenceLevel,
          },
        );
        return response.data;
      } catch (error) {
        console.error('Error adding thematic to guardrail:', error);
        throw error;
      }
    },
    [post],
  );

  const removeGuardrailThematic = useCallback(
    async (channelId, guardrailKey, thematicKey) => {
      try {
        const response = await del(
          `/v1/channels/${channelId}/guardrails/${guardrailKey}/thematics/${thematicKey}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error removing thematic from guardrail:', error);
        throw error;
      }
    },
    [del],
  );

  const updateGuardrailThematicConfidence = useCallback(
    async (channelId, guardrailKey, thematicKey, confidenceLevel) => {
      try {
        const response = await put(
          `/v1/channels/${channelId}/guardrails/${guardrailKey}/thematics/${thematicKey}`,
          {
            confidence_level: confidenceLevel,
          },
        );
        return response.data;
      } catch (error) {
        console.error('Error updating thematic confidence:', error);
        throw error;
      }
    },
    [put],
  );

  // Agent reports saved in the channel's chats; filters left out are not applied
  const getChannelReports = useCallback(
    async (channelId, { skip = 0, limit = 10, assistantKey, startDate, endDate } = {}) => {
      const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
      if (assistantKey) params.set('assistant_key', assistantKey);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      try {
        const response = await get(`/v1/channels/${channelId}/reports/all?${params.toString()}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching channel reports:', error);
        throw error;
      }
    },
    [get],
  );

  const exportChannelReports = useCallback(
    async (channelId, reportIds) => {
      try {
        const response = await post(`/v1/channels/${channelId}/reports/export`, reportIds, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'channel_reports.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting channel reports:', error);
        throw error;
      }
    },
    [post],
  );

  return (
    <ChannelApiContext.Provider
      value={{
        getChannels,
        getChannelById,
        getChannelSecretsById,
        createChannel,
        updateChannel,
        deleteChannel,
        getAssistants,
        getAssistantByChannelId,
        getChannelOptions,
        getTriggers,
        deleteTrigger,
        updateTrigger,
        createTrigger,
        getSearchTriggers,
        getThematics,
        getNotifications,
        getGuardrails,
        getGuardrailById,
        createGuardrail,
        updateGuardrail,
        deleteGuardrail,
        addGuardrailThematic,
        removeGuardrailThematic,
        updateGuardrailThematicConfidence,
        getChannelReports,
        exportChannelReports,
      }}
    >
      {children}
    </ChannelApiContext.Provider>
  );
}

export function useChannelApi() {
  const context = useContext(ChannelApiContext);
  if (!context) {
    throw new Error('useChannelApi must be used within a ChannelApiProvider');
  }
  return context;
}
